import React, { useEffect, useState } from 'react';
import axiosInstance from '../../utils/axiosInstance';
import {
  Button, Input, Modal, Upload, message, Table, Space, Form, Popconfirm, Select
} from 'antd';
import {
  UploadOutlined, PlusOutlined, EyeOutlined,
  EditOutlined, DeleteOutlined
} from '@ant-design/icons';

const CategoryManageritem = () => {
  const [form] = Form.useForm();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  /** Fetch categories */
  const fetchCategories = async () => {
    setLoading(true);
    try {
      const allRes = await axiosInstance.get('/categories-items');

      setCategories(allRes.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
      message.error("Failed to fetch categories");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  /** Add or Update */
  const handleFormSubmit = async (values) => {
    setSubmitLoading(true);
    try {
      const formData = new FormData();
      formData.append('name', values.name);
      if (values.description) formData.append('description', values.description);
      if (values.parentCategoryItem) formData.append('parentCategoryItem', values.parentCategoryItem);
      if (fileList.length > 0 && fileList[0].originFileObj) {
        formData.append('image', fileList[0].originFileObj);
      }

      if (editingCategory) {
        await axiosInstance.put(`/admin/categories-items/${editingCategory._id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        message.success('Category updated successfully');
      } else {
        await axiosInstance.post('/admin/categories-items', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        message.success('Category created successfully');
      }

      setIsModalOpen(false);
      form.resetFields();
      setEditingCategory(null);
      setFileList([]);
      fetchCategories();
    } catch (error) {
      console.error('Error saving category:', error);
      message.error(error?.response?.data?.message || 'Error saving category');
    } finally {
      setSubmitLoading(false);
    }
  };

  /** Edit */
  const handleEdit = (category) => {
    setEditingCategory(category);

    form.setFieldsValue({
      name: category.name,
      description: category.description,
      parentCategoryItem: category.parentCategoryItem?._id || null
    });

    if (category.image?.url) {
      setFileList([{
        uid: '-1',
        name: category.image.fileName || 'image.png',
        status: 'done',
        url: category.image.url
      }]);
    } else {
      setFileList([]);
    }

    setIsModalOpen(true);
  };

  /** Delete */
  const handleDelete = async (id) => {
    try {
      await axiosInstance.delete(`/admin/categories-items/${id}`);
      message.success('Category deleted successfully');
      fetchCategories();
    } catch (error) {
      message.error(error?.response?.data?.message || 'Error deleting category');
    }
  };

  /** View */
  const handleView = async (id) => {
    try {
      const res = await axiosInstance.get(`/categories-items/${id}`);
      Modal.info({
        title: `Category Details - ${res.data.name}`,
        content: (
          <div>
            <p><b>Slug:</b> {res.data.slug}</p>
            <p><b>Description:</b> {res.data.description || 'No description'}</p>
            <p><b>Parent:</b> {res.data.parentCategoryItem?.name || 'None (Parent Category)'}</p>
            <p><b>Created By:</b> {res.data.createdBy || 'Unknown'}</p>
            {res.data.image?.url && (
              <div style={{ marginTop: 10 }}>
                <p><b>Image:</b></p>
                <img src={res.data.image.url} alt="category" style={{ maxWidth: '200px' }} />
              </div>
            )}
          </div>
        ),
        width: 600,
      });
    } catch (err) {
      message.error("Failed to load category details");
    }
  };

  /** Upload */
  const handleUploadChange = ({ fileList: newFileList }) => setFileList(newFileList);

  const beforeUpload = (file) => {
    if (!file.type.startsWith("image/")) {
      message.error("Only image files allowed");
      return false;
    }
    if (file.size / 1024 / 1024 > 5) {
      message.error("Image size must be under 5MB");
      return false;
    }
    return false;
  };

  const handleModalCancel = () => {
    setIsModalOpen(false);
    form.resetFields();
    setFileList([]);
    setEditingCategory(null);
  };

  /** Parent Categories = no parentCategoryItem */
  const parentCategories = categories.filter(cat => !cat.parentCategoryItem);

  /** Subcategories = categories with parentCategoryItem */
  const subcategories = categories.filter(cat => cat.parentCategoryItem);

  /** Table Columns */

  const parentCategoryColumns = [
    { title: 'Category', dataIndex: 'name', key: 'name' },
    { title: 'Slug', dataIndex: 'slug', key: 'slug' },
    { title: 'Description', dataIndex: 'description', key: 'description' },
    {
      title: 'Image',
      render: (_, record) =>
        record.image?.url ?
          <img src={record.image.url} width="50" height="50" style={{ objectFit: "cover", borderRadius: 4 }} /> :
          <span>No image</span>
    },
    {
      title: 'Actions',
      render: (_, record) => (
        <Space>
          <Button icon={<EyeOutlined />} onClick={() => handleView(record._id)} />
          <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Popconfirm
            title="Delete?"
            onConfirm={() => handleDelete(record._id)}
          >
            <Button icon={<DeleteOutlined />} danger />
          </Popconfirm>
        </Space>
      )
    }
  ];

  const subcategoryColumns = [
    { title: 'Category', dataIndex: 'name', key: 'name' },
    { title: 'Slug', dataIndex: 'slug', key: 'slug' },
    { title: 'Description', dataIndex: 'description', key: 'description' },
    {
      title: 'Parent',
      render: (_, record) => record.parentCategoryItem?.name || "None"
    },
    {
      title: 'Image',
      render: (_, record) =>
        record.image?.url ?
          <img src={record.image.url} width="50" height="50" style={{ objectFit: "cover", borderRadius: 4 }} /> :
          <span>No image</span>
    },
    {
      title: 'Actions',
      render: (_, record) => (
        <Space>
          <Button icon={<EyeOutlined />} onClick={() => handleView(record._id)} />
          <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Popconfirm
            title="Delete?"
            onConfirm={() => handleDelete(record._id)}
          >
            <Button icon={<DeleteOutlined />} danger />
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 20 }}>
        <h2>Category Management</h2>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setEditingCategory(null);
            form.resetFields();
            setFileList([]);
            setIsModalOpen(true);
          }}
        >
          Add Category
        </Button>
      </div>

      <h3>Parent Categories</h3>
      <Table
        columns={parentCategoryColumns}
        dataSource={parentCategories}
        rowKey="_id"
        loading={loading}
        pagination={false}
      />

      <h3 style={{ marginTop: 30 }}>Subcategories</h3>
      <Table
        columns={subcategoryColumns}
        dataSource={subcategories}
        rowKey="_id"
        loading={loading}
        pagination={{ pageSize: 20 }}
      />

      {/* Modal */}
      <Modal
        open={isModalOpen}
        onCancel={handleModalCancel}
        title={editingCategory ? "Edit Category" : "Add New Category"}
        onOk={() => form.submit()}
        okText={editingCategory ? "Update Category" : "Create Category"}
        confirmLoading={submitLoading}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleFormSubmit}>

          <Form.Item name="name" label="Category Name" rules={[{ required: true }]}>
            <Input placeholder="Category Name" />
          </Form.Item>

          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} placeholder="Description" />
          </Form.Item>

          <Form.Item name="parentCategoryItem" label="Parent Category">
            <Select allowClear placeholder="Select parent (optional)">
              {parentCategories
                .filter(cat => cat._id !== editingCategory?._id) // prevent circular
                .map(cat => (
                  <Select.Option key={cat._id} value={cat._id}>
                    {cat.name}
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>

          <Form.Item label="Category Image">
            <Upload
              fileList={fileList}
              listType="picture-card"
              onChange={handleUploadChange}
              beforeUpload={beforeUpload}
              onRemove={() => setFileList([])}
              accept="image/*"
              maxCount={1}
            >
              {fileList.length === 0 && (
                <div>
                  <UploadOutlined />
                  <div>Upload Image</div>
                </div>
              )}
            </Upload>
          </Form.Item>

        </Form>
      </Modal>

    </div>
  );
};

export default CategoryManageritem;
